/**
 * Admin — health, basic stats, and pricing metadata (admin-only)
 */
import { useState, useEffect } from 'react';
import { fetchJson, postJson } from './api.js';

export default function AdminView({ user }) {
  const [activeTab, setActiveTab] = useState('overview');

  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState(null);

  const [systemHealth, setSystemHealth] = useState(null);
  const [systemHealthError, setSystemHealthError] = useState(null);

  const [priceMeta, setPriceMeta] = useState(null);
  const [reloadingPrices, setReloadingPrices] = useState(false);
  const [reloadMessage, setReloadMessage] = useState('');
  const [pricingError, setPricingError] = useState(null);

  const [urlCheckRunning, setUrlCheckRunning] = useState(false);
  const [urlCheckResult, setUrlCheckResult] = useState(null);
  const [urlCheckError, setUrlCheckError] = useState(null);

  const [packSet, setPackSet] = useState('OGN');
  const [packIterations, setPackIterations] = useState(10000);
  const [packOddsRunning, setPackOddsRunning] = useState(false);
  const [packOddsResult, setPackOddsResult] = useState(null);
  const [packOddsError, setPackOddsError] = useState(null);

  const [authSummary, setAuthSummary] = useState(null);
  const [authSummaryError, setAuthSummaryError] = useState(null);

  const [cardCoverage, setCardCoverage] = useState(null);
  const [cardCoverageError, setCardCoverageError] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) return;

    // Load basic system health for Overview / System tabs
    fetchJson('/api/health')
      .then(setHealth)
      .catch((e) => setHealthError(e.message));

    // Load pricing meta for Overview / Pricing tab
    fetchJson('/api/price-guide/meta')
      .then(setPriceMeta)
      .catch((e) => {
        setPricingError(e.message);
      });

    // Load admin system health for System tab
    fetchJson('/api/admin/system/health')
      .then(setSystemHealth)
      .catch((e) => setSystemHealthError(e.message));

    // Load auth summary for Users & Auth tab
    fetchJson('/api/admin/auth/summary')
      .then(setAuthSummary)
      .catch((e) => setAuthSummaryError(e.message));

    // Load card/set coverage for Data & Sets tab
    fetchJson('/api/admin/cards/coverage')
      .then(setCardCoverage)
      .catch((e) => setCardCoverageError(e.message));
  }, [user]);

  const handleReloadPrices = async () => {
    setReloadingPrices(true);
    setReloadMessage('');
    setPricingError(null);
    try {
      await postJson('/api/admin/price-guide/reload', { source: 'files', type: 'both' });
      setReloadMessage('Price data reload triggered successfully.');
      const meta = await fetchJson('/api/price-guide/meta');
      setPriceMeta(meta);
    } catch (e) {
      setPricingError(e.message || 'Failed to reload price data (admin access required).');
      setReloadMessage('');
    } finally {
      setReloadingPrices(false);
    }
  };

  const handleRunUrlCheck = async () => {
    setUrlCheckRunning(true);
    setUrlCheckError(null);
    try {
      const result = await postJson('/api/admin/price-guide/url-check', {});
      setUrlCheckResult(result);
    } catch (e) {
      setUrlCheckError(e.message || 'Failed to run Cardmarket URL check.');
    } finally {
      setUrlCheckRunning(false);
    }
  };

  const renderOverviewTab = () => (
    <section className="admin-section admin-overview">
      <h2>Overview</h2>
      <div className="admin-overview-grid">
        <div className="admin-card">
          <h3>System Health</h3>
          {healthError && <p className="admin-error">{healthError}</p>}
          {health ? (
            <ul>
              <li>Status: {health.status}</li>
              <li>MongoDB: {health.mongodb ? 'connected' : 'disconnected'}</li>
              <li>Redis: {health.redis ? 'connected' : 'disconnected'}</li>
            </ul>
          ) : (
            !healthError && <p>Loading system health…</p>
          )}
        </div>
        <div className="admin-card">
          <h3>Pricing Data</h3>
          {pricingError && <p className="admin-error">{pricingError}</p>}
          {priceMeta ? (
            <ul>
              <li>Products snapshot: {priceMeta.productsCreatedAt || 'unknown'}</li>
              <li>Price guide snapshot: {priceMeta.priceGuideCreatedAt || 'unknown'}</li>
              <li>Last reload: {priceMeta.loadedAt || 'never'}</li>
            </ul>
          ) : (
            !pricingError && <p>Price guide metadata not available yet.</p>
          )}
        </div>
      </div>
    </section>
  );

  const renderPricingTab = () => (
    <section className="admin-section admin-pricing">
      <h2>Pricing &amp; Economy</h2>
      {pricingError && <p className="admin-error">{pricingError}</p>}
      {priceMeta ? (
        <ul className="admin-pricing-meta">
          <li>Products snapshot: {priceMeta.productsCreatedAt || 'unknown'}</li>
          <li>Price guide snapshot: {priceMeta.priceGuideCreatedAt || 'unknown'}</li>
          <li>Last reload: {priceMeta.loadedAt || 'never'}</li>
        </ul>
      ) : (
        !pricingError && <p>Price guide metadata not available yet.</p>
      )}
      <button
        type="button"
        className="search-button"
        onClick={handleReloadPrices}
        disabled={reloadingPrices}
      >
        {reloadingPrices ? 'Reloading prices…' : 'Reload price data from files'}
      </button>
      {reloadMessage && <p className="admin-reload-message">{reloadMessage}</p>}
      <div className="admin-url-check">
        <h3>Cardmarket URL / Mapping Check</h3>
        <p>
          Runs a coverage check using current price metadata to see how many cards have Cardmarket
          mappings. Results are aggregated only; raw URLs and product IDs are not shown.
        </p>
        <button
          type="button"
          className="search-button"
          onClick={handleRunUrlCheck}
          disabled={urlCheckRunning}
        >
          {urlCheckRunning ? 'Running check…' : 'Run URL/mapping check'}
        </button>
        {urlCheckError && <p className="admin-error">{urlCheckError}</p>}
        {urlCheckResult && urlCheckResult.summary && (
          <div className="admin-url-check-summary">
            <p>Last run: {urlCheckResult.ranAt}</p>
            <ul>
              <li>Total cards: {urlCheckResult.summary.totalCards}</li>
              <li>Mapped cards: {urlCheckResult.summary.mappedCards}</li>
              <li>Unmapped cards: {urlCheckResult.summary.unmappedCards}</li>
              <li>
                Coverage:{' '}
                {Math.round((urlCheckResult.summary.coverage || 0) * 100)}
                %
              </li>
            </ul>
          </div>
        )}
      </div>
      <p className="admin-pricing-note">
        For local development, you can also run <code>npm run price:reload</code> in the root
        project to reload CardMarket price data from JSON files.
      </p>
    </section>
  );

  const renderPacksTab = () => (
    <section className="admin-section admin-packs">
      <h2>Packs &amp; Limited</h2>
      <div className="admin-packs-odds">
        <h3>Pack Odds Test</h3>
        <p>
          Run the same statistical odds test used in development/CI for a chosen set. This simulates
          many packs and compares observed results to expected odds.
        </p>
        <div className="admin-packs-form">
          <label htmlFor="pack-set-select">
            Set
            <select
              id="pack-set-select"
              value={packSet}
              onChange={(e) => setPackSet(e.target.value)}
            >
              <option value="OGN">OGN (Origins)</option>
              <option value="SFD">SFD (Spiritforged)</option>
            </select>
          </label>
          <label htmlFor="pack-iterations-input">
            Iterations
            <input
              id="pack-iterations-input"
              type="number"
              min={1000}
              max={50000}
              step={1000}
              value={packIterations}
              onChange={(e) => {
                const val = Number(e.target.value) || 0;
                setPackIterations(val);
              }}
            />
          </label>
          <button
            type="button"
            className="search-button"
            onClick={async () => {
              setPackOddsRunning(true);
              setPackOddsError(null);
              try {
                const result = await postJson('/api/admin/packs/odds-test', {
                  set: packSet,
                  iterations: packIterations
                });
                setPackOddsResult(result);
              } catch (e) {
                setPackOddsError(e.message || 'Failed to run pack odds test.');
              } finally {
                setPackOddsRunning(false);
              }
            }}
            disabled={packOddsRunning}
          >
            {packOddsRunning ? 'Running odds test…' : 'Run odds test'}
          </button>
        </div>
        {packOddsError && <p className="admin-error">{packOddsError}</p>}
        {packOddsResult && (
          <div className="admin-packs-results">
            <p>
              Last run for {packOddsResult.set} at {packOddsResult.ranAt} —{' '}
              {packOddsResult.passed ? 'within expected tolerances.' : 'outside expected tolerances.'}
            </p>
            <table className="admin-packs-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Observed</th>
                  <th>Expected</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Signed (slots 12–13)</td>
                  <td>{packOddsResult.stats?.signed}</td>
                  <td>{Math.round(packOddsResult.expected?.signed ?? 0)}</td>
                </tr>
                <tr>
                  <td>Showcase (slots 12–13)</td>
                  <td>{packOddsResult.stats?.showcase}</td>
                  <td>{Math.round(packOddsResult.expected?.showcase ?? 0)}</td>
                </tr>
                <tr>
                  <td>Epic (slots 12–13)</td>
                  <td>{packOddsResult.stats?.epic}</td>
                  <td>{Math.round(packOddsResult.expected?.epic ?? 0)}</td>
                </tr>
                <tr>
                  <td>Showcase Runes (slot 14)</td>
                  <td>{packOddsResult.stats?.showcaseRune}</td>
                  <td>{Math.round(packOddsResult.expected?.showcaseRune ?? 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );

  const renderAuthTab = () => (
    <section className="admin-section admin-auth">
      <h2>Users &amp; Auth</h2>
      {authSummaryError && <p className="admin-error">{authSummaryError}</p>}
      {authSummary ? (
        <div className="admin-auth-grid">
          <div className="admin-card">
            <h3>User Stats</h3>
            <ul>
              <li>Total users: {authSummary.totalUsers}</li>
              <li>New users (last 7 days): {authSummary.newUsersLast7Days}</li>
              <li>Admin users: {authSummary.adminUsers}</li>
            </ul>
          </div>
          <div className="admin-card">
            <h3>Discord Configuration</h3>
            <ul>
              <li>
                Client ID:{' '}
                {authSummary.hasDiscordClientId ? 'configured' : 'missing'}
              </li>
              <li>
                Client Secret:{' '}
                {authSummary.hasDiscordClientSecretConfigured ? 'configured' : 'missing'}
              </li>
              <li>
                Redirect URI:{' '}
                {authSummary.hasDiscordRedirectUri ? 'configured' : 'missing'}
              </li>
            </ul>
          </div>
        </div>
      ) : (
        !authSummaryError && <p>Loading auth summary…</p>
      )}
    </section>
  );

  const renderDataTab = () => (
    <section className="admin-section admin-data">
      <h2>Data &amp; Sets</h2>
      {cardCoverageError && <p className="admin-error">{cardCoverageError}</p>}
      {cardCoverage ? (
        <div className="admin-data-coverage">
          <p>
            Tracking per-set card counts, promo/signed/showcase coverage, and missing data for
            images and Cardmarket names.
          </p>
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Set</th>
                <th>Total</th>
                <th>Promo</th>
                <th>Signed</th>
                <th>Showcase</th>
                <th>Pack-eligible (true)</th>
                <th>Missing image</th>
                <th>Missing Cardmarket name</th>
              </tr>
            </thead>
            <tbody>
              {cardCoverage.sets.map((set) => (
                <tr key={set.setId}>
                  <td>{set.setName}</td>
                  <td>{set.totalCards}</td>
                  <td>{set.promoCount}</td>
                  <td>{set.signedCount}</td>
                  <td>{set.showcaseCount}</td>
                  <td>{set.packEligibleTrue}</td>
                  <td>{set.missingImageUrl}</td>
                  <td>{set.missingCardmarketName}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="admin-data-totals">
            <h3>Totals</h3>
            <ul>
              <li>Total cards: {cardCoverage.totals.totalCards}</li>
              <li>Promo cards: {cardCoverage.totals.totalPromo}</li>
              <li>Signed cards: {cardCoverage.totals.totalSigned}</li>
              <li>Showcase cards: {cardCoverage.totals.totalShowcase}</li>
              <li>Cards missing image: {cardCoverage.totals.totalMissingImage}</li>
              <li>
                Cards missing Cardmarket name: {cardCoverage.totals.totalMissingCardmarket}
              </li>
            </ul>
          </div>
        </div>
      ) : (
        !cardCoverageError && <p>Loading card and set coverage…</p>
      )}
    </section>
  );

  const renderSystemTab = () => (
    <section className="admin-section admin-system">
      <h2>System &amp; Diagnostics</h2>
      {(systemHealthError || healthError) && (
        <p className="admin-error">
          {systemHealthError || healthError}
        </p>
      )}
      {systemHealth ? (
        <div className="admin-system-grid">
          <div className="admin-card">
            <h3>Runtime</h3>
            <ul>
              <li>Status: {systemHealth.status}</li>
              <li>Node version: {systemHealth.nodeVersion}</li>
              <li>Environment: {systemHealth.env}</li>
              <li>Uptime (seconds): {systemHealth.uptimeSeconds}</li>
              <li>Timestamp: {systemHealth.timestamp}</li>
            </ul>
          </div>
          <div className="admin-card">
            <h3>MongoDB</h3>
            <ul>
              <li>Connected: {systemHealth.mongodb.connected ? 'yes' : 'no'}</li>
              <li>
                Ping (ms:{' '}
                {systemHealth.mongodb.pingMs != null
                  ? systemHealth.mongodb.pingMs
                  : 'n/a'}
                )
              </li>
            </ul>
          </div>
          <div className="admin-card">
            <h3>Redis</h3>
            <ul>
              <li>Configured: {systemHealth.redis.configured ? 'yes' : 'no'}</li>
              <li>Available: {systemHealth.redis.available ? 'yes' : 'no'}</li>
              <li>
                Ping (ms:{' '}
                {systemHealth.redis.pingMs != null ? systemHealth.redis.pingMs : 'n/a'}
                )
              </li>
            </ul>
          </div>
        </div>
      ) : (
        !systemHealthError && !healthError && <p>Loading system health…</p>
      )}
    </section>
  );

  if (!user || !user.isAdmin) {
    return (
      <div className="admin-view admin-view-unauthorized">
        <h1>Admin</h1>
        <p className="admin-error">
          You don&apos;t have access to the admin dashboard. If you think this is a mistake, please
          check that you are logged in with the correct account.
        </p>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="admin-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">
            Tools for monitoring RiftVision, managing pricing, validating packs, and reviewing data
            quality.
          </p>
        </div>
      </div>

      <nav className="admin-tabs" aria-label="Admin sections">
        <button
          type="button"
          className={activeTab === 'overview' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          className={activeTab === 'pricing' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('pricing')}
        >
          Pricing &amp; Economy
        </button>
        <button
          type="button"
          className={activeTab === 'packs' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('packs')}
        >
          Packs &amp; Limited
        </button>
        <button
          type="button"
          className={activeTab === 'auth' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('auth')}
        >
          Users &amp; Auth
        </button>
        <button
          type="button"
          className={activeTab === 'data' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('data')}
        >
          Data &amp; Sets
        </button>
        <button
          type="button"
          className={activeTab === 'system' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('system')}
        >
          System &amp; Diagnostics
        </button>
      </nav>

      <div className="admin-tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'pricing' && renderPricingTab()}
        {activeTab === 'packs' && renderPacksTab()}
        {activeTab === 'auth' && renderAuthTab()}
        {activeTab === 'data' && renderDataTab()}
        {activeTab === 'system' && renderSystemTab()}
      </div>
    </div>
  );
}
